import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent],
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.css'],
})
export class PrivacyPolicyComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('policyContent') policyContent!: ElementRef;

  lastUpdated: string = 'October 30, 2025';
  activeSection: string = '';
  private observer!: IntersectionObserver;
  private isScrolling = false;

  sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `
        <p>At <strong>Markado</strong>, protecting your privacy is really important to us. As two university students who started this marketplace platform in 2025, we understand how valuable your personal information is and we're committed to being completely transparent about how we handle it.</p>
        <p>This Privacy Policy explains what information we collect, why we need it, how we use it, and how we protect it. We've written this in plain language because we believe privacy policies shouldn't require a law degree to understand.</p>
        <p>Our marketplace serves mostly the North Macedonian community, connecting buyers and sellers in a safe, simple environment. We only collect information that helps us provide this service and keep our platform secure.</p>
      `,
    },
    {
      id: 'controller',
      title: 'Who We Are and Contact Information',
      content: `
    <p><strong>Markado</strong> is a small online marketplace operated by its founding team.</p>
    <ul>
      <li><strong>Data Controller:</strong> The Markado founding team (operating as a freelancer startup)</li>
      <li><strong>Location:</strong> Tetovo, North Macedonia</li>
      <li><strong>Contact:</strong> You can reach us through the <a href="/contact-us">Contact Us</a> page (available to logged-in users).</li>
    </ul>
    <p>We operate as independent individuals rather than a registered company. For the purposes of EU and North Macedonian data protection law, Markado’s founding team acts as the data controller responsible for your personal information.</p>
  `,
    },
    {
      id: 'information-we-collect',
      title: 'What Information We Collect',
      content: `
        <p>We collect different types of information to run our marketplace and provide you with the best possible experience. Here's exactly what we collect and why:</p>

        <p><strong>Account Information:</strong></p>
        <p>When you create an account, we collect the following information:</p>
        <ul>
          <li>Name, email address, and phone number - used to set up your account and allow customers to contact you.</li>
          <li>Password - stored securely and encrypted for your protection.</li>
          <li>Marketing preferences - your consent to receive occasional marketing emails, including relevant listings and updates from other sellers.</li>
        </ul>

        <p><strong>When You Sell Items:</strong></p>
        <ul>
          <li>Product photos and information - used to show potential buyers</li>
        </ul>

        <p><strong>When You Use Our Platform:</strong></p>
        <ul>
          <li>Messages between buyers and sellers - used to potentially resolve issues</li>
          <li>Items you favorite or save - used to show them in your account</li>
        </ul>

        <p><strong>Payment Information:</strong></p>
        <ul>
          <li>We don't store your credit card details - these are handled securely by our payment processor</li>
          <li>We securely keep records of your account top ups - used to ensure accurate accounting.</li>
        </ul>

        <p><strong>Local Storage:</strong></p>
        <p>Our web application uses your browser's local storage to:</p>
        <ul>
          <li>Remember your login status - so you don't have to log in every time</li>
          <li>Save your preferences - language, currency, and so on</li>
        </ul>
        <p>This information stays entirely on your device and isn't automatically sent to our servers. We only receive this information when you take specific actions (like saving a favorite item to your account).</p>

        <p><strong>What We DON'T Collect:</strong></p>
        <ul>
          <li>We don't track you across other websites</li>
          <li>We don't collect sensitive personal information like health data or political views</li>
          <li>We don't use facial recognition or biometric data</li>
          <li>We don't access your device's contacts, photos, or other apps without permission</li>
          <li>We don't use cookies for tracking or advertising</li>
        </ul>
      `,
    },
    {
      id: 'how-we-use-information',
      title: 'How We Use Your Information',
      content: `
        <p>We use your information for specific purposes that directly relate to providing our marketplace service. Here's how:</p>

        <p><strong>Running the Marketplace:</strong></p>
        <ul>
          <li>Creating and managing your account</li>
          <li>Showing your listings to potential buyers</li>
          <li>Enabling communication between buyers and sellers</li>
          <li>Processing payments and handling transactions</li>
          <li>Providing customer support when you need help</li>
          <li>Helping resolve disputes between users</li>
        </ul>

        <p><strong>Keeping the Platform Safe:</strong></p>
        <ul>
          <li>Monitoring for suspicious or fraudulent activity</li>
          <li>Preventing spam and abuse</li>
          <li>Ensuring compliance with our community guidelines</li>
          <li>Protecting against security threats</li>
        </ul>

        <p><strong>Improving Our Service:</strong></p>
        <ul>
          <li>Understanding which features are most useful</li>
          <li>Fixing bugs and technical issues</li>
          <li>Making search results more relevant</li>
          <li>Developing new features based on user needs</li>
          <li>Optimizing the platform's performance</li>
        </ul>

        <p><strong>Communication:</strong></p>
        <ul>
          <li>Sending important notifications about your account or transactions</li>
          <li>Alerting you to messages from other users</li>
          <li>Updating you about changes to our service or policies</li>
          <li>Sending promotional emails (only if you opt in)</li>
        </ul>

        <p><strong>Legal Compliance:</strong></p>
        <ul>
          <li>Complying with North Macedonian and EU laws</li>
          <li>Responding to legal requests when required</li>
          <li>Maintaining records for tax and accounting purposes</li>
        </ul>
      `,
    },
    {
      id: 'legal-basis',
      title: 'Legal Basis for Processing',
      content: `
    <p>Under the EU General Data Protection Regulation (GDPR), we must explain the legal grounds on which we process your personal data. We rely on the following bases:</p>
    <ul>
      <li><strong>Performance of a Contract:</strong> To provide and manage your Markado account, enable transactions, and deliver marketplace services.</li>
      <li><strong>Legitimate Interest:</strong> To keep the platform secure, prevent fraud, and improve our features and performance.</li>
      <li><strong>Consent:</strong> When you choose to receive marketing emails or other optional communications.</li>
      <li><strong>Legal Obligation:</strong> To comply with tax, accounting, and applicable legal requirements.</li>
    </ul>
  `,
    },
    {
      id: 'information-sharing',
      title: 'When We Share Your Information',
      content: `
        <p>We're very careful about sharing your information. Here's exactly when and why we might share it:</p>

        <p><strong>With Other Users (By Design):</strong></p>
        <ul>
          <li>Your public profile information and listings are visible to all users</li>
          <li>When you contact a seller or buyer, they can see your information</li>
        </ul>

        <p><strong>With Service Providers (Limited):</strong></p>
        <p>We work with a small number of trusted companies to help run our platform:</p>
        <ul>
          <li>Payment processors (to handle secure transactions)</li>
          <li>Cloud hosting providers (to store data securely)</li>
          <li>Email service providers (to send notifications)</li>
        </ul>
        <p>All these companies are required to protect your data and only use it for the specific services they provide to us.</p>

        <p><strong>For Legal Reasons (When Required):</strong></p>
        <ul>
          <li>When required by North Macedonian or EU law</li>
          <li>To respond to valid legal requests from authorities</li>
          <li>To protect our users' safety or prevent illegal activity</li>
          <li>To enforce our terms of service</li>
        </ul>

        <p><strong>Business Changes:</strong></p>
        <ul>
          <li>If in the future Markado becomes a registered company or merges with another project, user information may be transferred to the new operators.</li>
          <li>We'll notify all users in advance and explain how their data will be handled.</li>
        </ul>

        <p><strong>What We WON'T Do:</strong></p>
        <ul>
          <li>We won't sell your personal information to advertisers or data brokers</li>
          <li>We won't share your information for unrelated marketing purposes</li>
          <li>We won't give your data to companies for their own business use</li>
          <li>We won't track you across the internet for advertising</li>
        </ul>
      `,
    },
    {
      id: 'data-security',
      title: 'How We Protect Your Information',
      content: `
        <p>As a tech startup, we take security seriously. We've implemented strong measures to protect your information, even though we're still a small team:</p>

        <p><strong>Technical Protection:</strong></p>
        <ul>
          <li>All data is encrypted when transmitted between your device and our servers</li>
          <li>Passwords are securely hashed and can't be read by anyone, including us</li>
          <li>Our website uses HTTPS for all pages</li>
          <li>Regular security updates and patches</li>
          <li>Secure cloud hosting with reputable providers</li>
        </ul>

        <p><strong>Access Controls:</strong></p>
        <ul>
          <li>Only the necessary team members have access to user data</li>
          <li>We use strong authentication for all admin access</li>
          <li>Regular review of who has access to what</li>
        </ul>

        <p><strong>Monitoring and Response:</strong></p>
        <ul>
          <li>We monitor our systems for unusual activity</li>
          <li>Rapid response procedures for any security incidents</li>
          <li>Regular security assessments as we grow</li>
        </ul>

        <p><strong>Your Role in Security:</strong></p>
        <ul>
          <li>Use a strong, unique password for your Markado account</li>
          <li>Don't share your login credentials with anyone</li>
          <li>Log out when using shared or public computers</li>
          <li>Report any suspicious activity to us immediately</li>
          <li>Keep your contact information up to date</li>
        </ul>

        <p><strong>If There's a Problem:</strong> If we ever discover a security breach that affects your personal information, we will notify you without undue delay via email and explain what happened, what data may be affected, and what steps we are taking.</p>
      `,
    },
    {
      id: 'your-rights',
      title: 'Your Privacy Rights',
      content: `
    <p>You have important rights under EU and North Macedonian data protection laws. We've made it easy to exercise these rights:</p>

    <p><strong>Access & Portability:</strong></p>
    <ul>
      <li>View and edit most of your information in your account settings.</li>
      <li>Request a copy of your personal data in a portable format.</li>
    </ul>

    <p><strong>Correction:</strong></p>
    <ul>
      <li>Update your profile information anytime.</li>
      <li>Correct mistakes in your listings or account details.</li>
    </ul>

    <p><strong>Deletion:</strong></p>
    <ul>
      <li>Delete your account anytime by contacting us.</li>
      <li>Remove listings you no longer want displayed.</li>
    </ul>

    <p><strong>Restriction & Objection:</strong></p>
    <ul>
      <li>Request that we restrict certain processing in specific circumstances.</li>
      <li>Object to processing based on our legitimate interests, and we will stop unless we have compelling grounds.</li>
    </ul>

    <p><strong>Marketing Preferences:</strong></p>
    <ul>
      <li>Opt out of promotional emails at any time (you'll still receive important transactional messages).</li>
      <li>Withdraw consent for optional features without affecting your account.</li>
    </ul>

    <p><strong>How to Exercise Your Rights:</strong></p>
    <ul>
      <li>Most things can be done through your account settings but for anything else, don't hesitate to contact us.</a>.</li>
      <li>We may need to verify your identity for security.</li>
      <li>We aim to respond within 30 days.</li>
    </ul>

    <p><strong>Complaints:</strong> You also have the right to lodge a complaint with your local data protection authority. We encourage you to contact us first so we can try to resolve your concern quickly.</p>
  `,
    },
    {
      id: 'data-retention',
      title: 'How Long We Keep Your Information',
      content: `
    <p>We only keep your information for as long as necessary to operate our marketplace or meet legal requirements. When you delete your account, we permanently remove all related data from our systems.</p>

    <p><strong>Automatic Cascade Deletion:</strong></p>
    <ul>
      <li>When you delete your account, all data connected to it - including your listings, messages, saved items, and profile information - is automatically and permanently deleted.</li>
      <li>This process is irreversible and ensures that no other users can access or recover your information after deletion.</li>
    </ul>

    <p><strong>Limited Legal Exceptions:</strong></p>
    <ul>
      <li>We may retain minimal transaction or accounting records if required by North Macedonian or EU financial laws (for example, for tax compliance). These records contain no user-identifiable data and are deleted after the legally required retention period.</li>
    </ul>

    <p><strong>Inactive Accounts:</strong></p>
    <ul>
      <li>If you stop using your account without deleting it, we may remove it after a period of prolonged inactivity, following advance notice to your registered email.</li>
    </ul>

    <p><strong>Secure Deletion:</strong></p>
    <ul>
      <li>All deletions are performed through automated systems that ensure permanent data removal.</li>
      <li>Deleted data cannot be recovered by anyone, including our team.</li>
      <li>We regularly audit this process to verify compliance with our privacy commitments.</li>
    </ul>
  `,
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      content: `
        <p>As a North Macedonian-based platform operated by independent founders, most of your data stays within North Macedonia and the EU. However, some of our service providers may be located outside this region:</p>

        <p><strong>Where Your Data Might Go:</strong></p>
        <ul>
          <li>Cloud hosting providers may have servers in the EU, US, or other countries</li>
          <li>Payment processors may transfer data internationally for transaction processing</li>
          <li>Email and communication services may use international infrastructure</li>
        </ul>

        <p><strong>How We Protect International Transfers:</strong></p>
        <ul>
          <li>We only use providers that meet EU data protection standards</li>
          <li>We require contractual protections for all international transfers</li>
          <li>We ensure equivalent levels of protection regardless of location</li>
          <li>We regularly review our providers' privacy and security practices</li>
        </ul>

        <p><strong>Your Rights:</strong></p>
        <ul>
          <li>You have the same privacy rights regardless of where data is processed</li>
          <li>You can request information about where your data is stored</li>
        </ul>

        <p>When we transfer data outside the EU, we rely on safeguards such as the European Commission's <strong>Standard Contractual Clauses (SCCs)</strong> or equivalent legal mechanisms that ensure your information remains protected at the same level as within the EU.</p>
      `,
    },
    {
      id: 'children',
      title: "Children's Privacy",
      content: `
        <p><strong>Age Requirement:</strong> Our marketplace is designed for adults. You must be at least 18 years old to create an account and use our services. This is because buying and selling requires legal capacity to enter into contracts.</p>

        <p><strong>No Collection from Children:</strong></p>
        <ul>
          <li>We don't knowingly collect personal information from anyone under 18</li>
          <li>We don't target our marketing or services to children</li>
          <li>If we discover we've collected information from a child, we'll delete it immediately</li>
        </ul>

        <p><strong>What Parents Should Know:</strong></p>
        <ul>
          <li>If you believe your child has created an account, please contact us immediately</li>
          <li>We'll work with you to remove any information and close the account</li>
          <li>We don't require extensive personal information, which limits exposure</li>
        </ul>

        <p><strong>If You're Under 18:</strong></p>
        <ul>
          <li>Please don't create an account or use our services</li>
          <li>Ask a parent or guardian to help you if you're interested in online selling</li>
          <li>We're happy to explain our policies to parents who have questions</li>
        </ul>
      `,
    },
    {
      id: 'automated-decisions',
      title: 'Automated Decision-Making',
      content: `
    <p>We do not use automated decision-making or profiling that produces legal or similarly significant effects about you. If this ever changes, we will update this policy and explain your rights.</p>
  `,
    },
    {
      id: 'changes',
      title: 'Changes to This Privacy Policy',
      content: `
        <p><strong>Why We Might Update This Policy:</strong></p>
        <p>As our marketplace grows and evolves, we may need to update this privacy policy to reflect:</p>
        <ul>
          <li>New features we add to the platform</li>
          <li>Changes in privacy laws or regulations</li>
          <li>New service providers we work with</li>
          <li>Feedback from users about clarity or completeness</li>
          <li>Changes in how we handle data as we grow</li>
        </ul>

        <p><strong>How We'll Notify You:</strong></p>
        <ul>
          <li><strong>Minor Updates:</strong> Updated date at the top of this policy</li>
          <li><strong>Significant Changes:</strong> Email notification to all users plus a prominent notice on our website</li>
          <li><strong>Major Changes:</strong> In-app notifications and possibly a consent request</li>
        </ul>

        <p><strong>What We Consider Major:</strong></p>
        <ul>
          <li>New types of personal information we collect</li>
          <li>Changes to how we use or share your information</li>
          <li>Changes to your rights or how to exercise them</li>
          <li>New third-party services that access your data</li>
        </ul>

        <p><strong>Your Options:</strong></p>
        <ul>
          <li>Contact us with questions about any changes</li>
          <li>Delete your account if you disagree with the changes</li>
        </ul>

        <p><strong>Continued Use:</strong> By continuing to use our marketplace after changes take effect, you're agreeing to the updated policy. If you don't agree, you can delete your account or stop using our services.</p>

        <p><strong>Our Commitment:</strong> Even as we grow from a small startup to a larger platform, we're committed to maintaining the same level of transparency and respect for your privacy. We'll always explain our practices clearly and give you control over your information.</p>
      `,
    },
    {
      id: 'effective-date',
      title: 'Effective Date',
      content: `
    <p>This Privacy Policy is effective as of <strong>October 30, 2025</strong>. We encourage you to review it periodically for any updates.</p>
  `,
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.activeSection = this.sections[0]?.id || '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.policyContent && this.policyContent.nativeElement) {
        this.policyContent.nativeElement.classList.add('loaded');
      }
      this.setupIntersectionObserver();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {}

  private setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    };

    this.observer = new IntersectionObserver((entries) => {
      if (this.isScrolling) return;

      const visibleEntries = entries.filter((entry) => entry.isIntersecting);

      if (visibleEntries.length > 0) {
        const firstVisible = visibleEntries[0];
        const sectionId = firstVisible.target.id;

        if (sectionId && this.activeSection !== sectionId) {
          this.activeSection = sectionId;
        }
      }
    }, options);

    this.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        this.observer.observe(element);
      }
    });
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      this.isScrolling = true;
      this.activeSection = sectionId;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      setTimeout(() => {
        this.isScrolling = false;
      }, 1000);
    }
  }

  isActive(sectionId: string): boolean {
    return this.activeSection === sectionId;
  }

  trackBySection(index: number, section: any): string {
    return section.id;
  }
}
